import { useEffect, useState, useMemo } from "react"
import * as Yup from "yup"
import axios from "axios"
import { motion } from "framer-motion"
import classNames from "classnames"
import { ErrorMessage, useField, useFormikContext } from "formik"
import QRCode from "react-qr-code"
import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js"

import { API } from "utils/api"
import { trackConsultationSubmit } from "utils/tracking"

import { WysiwygText } from "components/shared"
import { Field, Form } from "components/form"

import { ConsultationFrontIcon, ConsultationLeftIcon, ConsultationRightIcon, ConsultationTopIcon } from "assets/icons"
import { useAnimation } from "utils/useAnimation"
import { useRouter } from "next/router"
import { debounce } from "lodash"
import * as Sentry from "@sentry/nextjs"

import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/high-res.css"

const ImageLabel = () => {
  const [QRExpanded, setQRExpanded] = useState(false)
  const [qrValue, setQRValue] = useState("")
  const { values } = useFormikContext()

  const updateQRValue = useMemo(
    () =>
      debounce((newValues) => {
        const params = new URLSearchParams()
        if (newValues.name) params.append("name", newValues.name)
        if (newValues.email) params.append("email", newValues.email)
        if (newValues.phone) params.append("phone", newValues.phone)
        if (newValues.dateOfBirth) params.append("dateOfBirth", newValues.dateOfBirth)
        if (newValues.referralSource) params.append("referralSource", newValues.referralSource)
        if (newValues.message) params.append("message", newValues.message)

        setQRValue(`https://www.tirnanogclinic.com/consultation?${params.toString()}`)
      }, 500),
    []
  )

  useEffect(() => {
    updateQRValue(values)
  }, [values, updateQRValue])

  return (
    <label className="text-lg font-medium flex flex-col gap-1">
      <span>Images</span>
      <div className="hidden xl:flex items-end -mt-4 cursor-pointer " onClick={() => setQRExpanded(!QRExpanded)}>
        <div className="text-sm font-medium select-none ">
          Want to upload images via mobile? <span className="underline">Click</span> and scan QR code here
        </div>
        <div
          className={classNames(
            "-ml-2 text-blue w-10 h-10 relative scale-[0.1] hover: z-10 transition-transform duration-300 cursor-pointer",
            QRExpanded && "scale-[0.5]"
          )}
        >
          <QRCode value={qrValue} fgColor="currentColor" bgColor="#FFF" />
        </div>
      </div>
    </label>
  )
}

const ImageField = ({ name, label, icon: Icon }) => {
  const [, meta, helpers] = useField(name)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isConverting, setIsConverting] = useState(false)
  const [error, setError] = useState(null)

  const resizeImage = async (img, maxWidth = 1920, maxHeight = 1080) => {
    try {
      const canvas = document.createElement("canvas")
      let width = img.width
      let height = img.height

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round(height * (maxWidth / width))
          width = maxWidth
        }
      } else {
        if (height > maxHeight) {
          width = Math.round(width * (maxHeight / height))
          height = maxHeight
        }
      }

      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext("2d")

      if (!ctx) {
        throw new Error("Could not get canvas context")
      }

      ctx.drawImage(img, 0, 0, width, height)

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error("Failed to create blob from canvas"))
            }
          },
          "image/jpeg",
          0.85 // Slightly reduced quality for better file size
        )
      })
    } catch (error) {
      console.error("Error in resizeImage:", error)
      throw new Error(`Image resize failed: ${error.message}`)
    }
  }

  const handleFileChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    setIsConverting(true)
    setError(null)

    try {
      // Validate file size (10MB)
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
      if (file.size > MAX_FILE_SIZE) {
        throw new Error("File size exceeds 10MB limit")
      }

      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/heic", "image/heif"]
      const isValidType =
        validTypes.includes(file.type) || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")

      if (!isValidType) {
        throw new Error("Invalid file type. Please upload a JPEG, PNG, or HEIC image")
      }

      let processedFile = file
      // Handle HEIC/HEIF conversion
      const isHEIC =
        file.type === "image/heic" ||
        file.type === "image/heif" ||
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif")

      if (isHEIC && typeof window !== "undefined") {
        try {
          const heic2any = (await import("heic2any")).default
          const result = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.85,
          })
          processedFile = new File([result], file.name.replace(/\.(heic|heif)$/i, ".jpg"), {
            type: "image/jpeg",
            lastModified: Date.now(),
          })
        } catch (error) {
          console.error("HEIC conversion failed:", error)
          throw new Error("Failed to convert HEIC image. Please try uploading a JPEG or PNG instead")
        }
      }

      // Create image preview and resize
      const img = new Image()
      const loadImagePromise = new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = () => reject(new Error("Failed to load image"))
      })

      img.src = URL.createObjectURL(processedFile)
      await loadImagePromise

      const resizedBlob = await resizeImage(img)
      const finalFile = new File([resizedBlob], processedFile.name, {
        type: "image/jpeg",
        lastModified: Date.now(),
      })

      // Validate final file size
      if (finalFile.size > MAX_FILE_SIZE) {
        throw new Error("Processed image still exceeds size limit. Please try a smaller image")
      }

      helpers.setValue(finalFile)
      setPreviewUrl(URL.createObjectURL(finalFile))

      // Clean up object URLs
      URL.revokeObjectURL(img.src)
    } catch (error) {
      console.error("Error processing file:", error)
      setError(error.message)
      helpers.setValue(null)
      setPreviewUrl(null)

      // Report to Sentry if it's not a user-facing error
      if (!error.message.includes("size") && !error.message.includes("type")) {
        Sentry.captureException(error, {
          extra: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            component: "ImageField",
          },
        })
      }
    } finally {
      setIsConverting(false)
    }
  }

  return (
    <div key={name}>
      <div className="flex justify-between">
        <span className="font-medium">{label}</span>
        <ErrorMessage name={name} component="span" className="text-red-500 text-sm font-semibold" />
      </div>
      <label
        htmlFor={`${name}-img`}
        className={classNames(
          "image-wrapper cursor-pointer relative mt-3 border border-grey-600 h-96 md:h-56 flex items-center justify-center overflow-hidden",
          meta.error && meta.touched && "border-red-500"
        )}
      >
        {!previewUrl ? (
          <button type="button" className="choose-file-button pointer-events-none">
            {Icon && <Icon className="w-1/2 h-1/2 lg:w-1/2 lg:h-1/2 mx-auto opacity-70" />}
            <div className="text-[50px] absolute -top-3 left-2 opacity-70">+</div>
          </button>
        ) : (
          <img src={previewUrl} className="object-cover w-full h-full" alt={label} />
        )}
      </label>
      <div className={classNames("mt-2 flex justify-between items-center", !previewUrl && !error && "invisible")}>
        {error && <span className="text-red-500 text-sm">{error}</span>}
        <div className="flex gap-4 text-sm">
          <label className="cursor-pointer font-medium" htmlFor={`${name}-img`}>
            Edit
          </label>
          <div
            className="cursor-pointer font-medium text-red-500 hover:text-red-600"
            onClick={() => {
              helpers.setValue(null)
              setPreviewUrl(null)
              setError(null)
            }}
          >
            Delete
          </div>
        </div>
      </div>
      <input
        type="file"
        id={`${name}-img`}
        name={`${name}-img`}
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={handleFileChange}
        disabled={isConverting}
      />
      {/* {isConverting && <div className="mt-2 text-sm text-blue-600">Converting image... Please wait...</div>} */}
    </div>
  )
}

const HowDidYouHearField = () => {
  const [{ value }, { error, touched }, { setValue }] = useField("referralSource")
  const [field] = useField("referralSourceDescription")
  const { variants } = useAnimation()

  const options = [
    "Google",
    "Facebook",
    "Instagram",
    "Tiktok",
    "Newspaper",
    "Magazine",
    "Radio",
    "TV",
    "Advert on Dart",
    "Word of mouth",
    "Barber",
    "Other",
  ]

  const showDescription = value === "Barber" || value === "Other"

  return (
    <>
      <motion.div className="flex flex-col space-y-2 text-lg" variants={variants}>
        <label className="font-medium text-lg">How Did You Hear About Us?</label>
        <div
          className={classNames(
            "relative flex justify-between gap-x-3 border-b border-grey-600 pt-1 pb-2 font-semibold",
            error && touched && "border-red-500 !text-red-500",
            value === "" && "text-grey-400"
          )}
        >
          <select
            className={classNames("flex-1 min-h-[28px] cursor-pointer outline-none", value === "" && "font-medium")}
            onChange={(event) => setValue(event.target.value)}
          >
            <option disabled selected>
              Via
            </option>
            {options.map((option, index) => (
              <option key={index}>{option}</option>
            ))}
          </select>
          <ErrorMessage name="referralSource" component="span" className={classNames("text-red-500", "text-sm self-center")} />
        </div>
      </motion.div>
      {showDescription && (
        <div className="mt-4" key="referralSourceDescription">
          <Field
            {...field}
            className="!opacity-100 !translate-y-0"
            name="referralSourceDescription"
            label={value === "Barber" ? "Which Barber?" : "Please Specify"}
            placeholder="Please provide more details"
          />
        </div>
      )}
    </>
  )
}

const DOBField = ({ name, label, placeholder, type = "text", onChange, ...props }) => {
  const { variants } = useAnimation()

  const [field, meta, helpers] = useField(name)

  const handleChange = (e) => {
    if (name === "dateOfBirth") {
      const formatted = formatDate(e.target.value)
      helpers.setValue(formatted)
    } else {
      field.onChange(e)
    }
  }

  return (
    <motion.div className="flex flex-col space-y-2 text-lg" variants={variants}>
      <label className="font-medium" htmlFor={name}>
        {label}
      </label>
      <div
        className={classNames(
          "relative flex justify-between gap-x-3 border-b border-grey-600 pt-1 pb-2",
          meta.error && meta.touched && "border-red-500"
        )}
      >
        <input
          {...field}
          {...props}
          id={name}
          type={type}
          placeholder={placeholder}
          onChange={handleChange}
          className={classNames("flex-1 min-h-[28px] outline-none", meta.error && meta.touched && "placeholder:text-red-500")}
        />
        <ErrorMessage name={name} component="span" className="text-red-500 text-sm self-end font-semibold text-right" />
      </div>
    </motion.div>
  )
}

export const PhoneNumberField = () => {
  const [field, meta, helpers] = useField("phone")
  const { variants } = useAnimation()

  return (
    <motion.div className="flex flex-col space-y-2 text-lg" variants={variants}>
      <label className="font-medium">Phone Number (WhatsApp)</label>
      <div
        className={classNames(
          "relative pt-1 pb-2 flex justify-between items-center gap-x-3 border-b border-grey-600",
          meta.error && meta.touched && "border-red-500"
        )}
      >
        <PhoneInput
          country={"ie"}
          enableSearch={true}
          preferredCountries={["ie", "gb", "us"]}
          value={field.value}
          onChange={(value) => helpers.setValue(value)}
          placeholder="+353 87 1234567"
          inputProps={{
            required: true,
          }}
        />
        <ErrorMessage
          name="phone"
          component="span"
          className="text-red-500 text-sm self-center font-semibold text-right flex-shrink max-w-[80px] sm:max-w-none"
        />
      </div>
    </motion.div>
  )
}

const formatDate = (value) => {
  if (value.includes("/")) {
    const cleaned = value.replace(/[^\d/]/g, "")

    const parts = cleaned.split("/")
    const numbers = parts.join("")
    const day = numbers.slice(0, 2)
    const month = numbers.slice(2, 4)
    const year = numbers.slice(4, 8)

    if (numbers.length < 3) return day + "/"
    if (numbers.length < 5) {
      return month.length === 2 && value.endsWith("/") ? `${day}/${month}/` : `${day}/${month}`
    }

    return value.endsWith("/") ? `${day}/${month}/` : `${day}/${month}/${year}`
  }

  // Handle automatic formatting for pure number input
  const numbers = value.replace(/\D/g, "")
  if (!numbers) return value

  const day = numbers.slice(0, 2)
  const month = numbers.slice(2, 4)
  const year = numbers.slice(4, 8)

  if (numbers.length < 3) return day
  if (numbers.length < 5) return `${day}/${month}`
  return `${day}/${month}/${year}`
}

const ErrorScroller = () => {
  const { errors, submitCount } = useFormikContext()

  useEffect(() => {
    if (Object.keys(errors).length > 0 && submitCount > 0) {
      const firstErrorField = Object.keys(errors)[0]
      const element = document.querySelector(`[name="${firstErrorField}"]`)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }, [submitCount, errors])

  return null
}

const OnlineConsultationForm = ({ description, successMessage }) => {
  const [formComplete, setFormComplete] = useState(false)
  const { variants } = useAnimation()
  const { animation } = useAnimation("container", 0.1)

  const router = useRouter()
  const { query } = router

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Required")
      .min(2, "Name is too short")
      .matches(/^[\w\s]+ [\w\s]+$/, "Please Enter Your Full Name"),
    email: Yup.string().email("Invalid email").required("Required"),
    phone: Yup.string()
      .required("Required")
      .test("valid-phone", "Invalid number", function (value) {
        if (!value) return false
        try {
          // Add '+' if it's missing (libphonenumber-js requires it)
          const phoneNumberString = value.startsWith("+") ? value : `+${value}`
          return isValidPhoneNumber(phoneNumberString)
        } catch (error) {
          return false
        }
      }),
    dateOfBirth: Yup.string()
      .required("Required")
      .matches(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/, "Invalid date format (DD/MM/YYYY)")
      .test("valid-date", "Please enter a valid date", function (value) {
        if (!value) return false
        const [day, month, year] = value.split("/").map(Number)
        const date = new Date(year, month - 1, day)
        return (
          date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year && date <= new Date() // Ensures date is not in the future
        )
      }),
    referralSource: Yup.string().required("Required"),
    referralSourceDescription: Yup.string().when("referralSource", {
      is: (value) => value === "Barber",
      then: () => Yup.string().required("Please provide details"),
      otherwise: () => Yup.string(),
    }),
    imageFront: Yup.mixed().required("Front image is required"),
    imageTop: Yup.mixed().required("Top image is required"),
    imageLeft: Yup.mixed().required("Left image is required"),
    imageRight: Yup.mixed().required("Right image is required"),
    message: Yup.string(),
  })

  return (
    <motion.div className="space-y-10" {...animation}>
      <WysiwygText text={description} animate />
      <Form
        key="2"
        initialValues={{
          name: query?.name || "",
          email: query?.email || "",
          phone: query?.phone || "",
          dateOfBirth: query?.dateOfBirth || "",
          referralSource: query?.referralSource || "",
          referralSourceDescription: query?.referralSourceDescription || "",
          message: query?.message || "",
          imageFront: null,
          imageTop: null,
          imageLeft: null,
          imageRight: null,
        }}
        validationSchema={validationSchema}
        formComplete={formComplete}
        successMessage={successMessage}
        onSubmit={async (values, { setSubmitting, setStatus, resetForm }) => {
          try {
            setStatus()
            setSubmitting(true)

            const nameParts = values.name.split(" ")
            const firstName = nameParts?.[0]
            const lastName = nameParts?.slice(1).join(" ")

            const patient_phone_numbers = [
              {
                number: `+${values.phone}`,
                normalized_number: `+${values.phone.toString().replace(/\s+/g, "")}`,
                phone_type: "mobile",
              },
            ]
            const valuesToSubmit = {
              data: {
                ...values,
                referral_source: values.referralSource,
                referral_source_description: values.referralSourceDescription,
                date_of_birth: values.dateOfBirth,
                phone: values.phone.toString(),
                first_name: firstName,
                last_name: lastName,
                patient_phone_numbers,
                patient_type: "hair",
              },
            }

            // TODO: Find or create to do re-submission
            const patientResponse = await axios.post(`${API}/api/cliniko/patients/find-or-create`, valuesToSubmit)
            const patient = patientResponse.data

            // Create treatment note
            const treatmentNoteResponse = await axios.post(`${API}/api/cliniko/treatment-notes`, {
              data: {
                patient_id: patient.id,

                treatment_note_template_id: process.env.NEXT_PUBLIC_CLINIKO_TREATMENT_NOTE_TEMPLATE_ID,
                title: "Online Consultation",
                draft: true,
                content: {
                  sections: [
                    {
                      name: "Form Response",
                      questions: [
                        {
                          name: "Message",
                          type: "paragraph",
                          answer: values.message,
                        },
                      ],
                    },
                  ],
                },
              },
            })

            // Upload images if they exist
            const imagesToUpload = ["imageFront", "imageTop", "imageLeft", "imageRight"]
            for (const imageName of imagesToUpload) {
              if (values[imageName]) {
                console.log(`Preparing to upload ${imageName}...`)
                const formData = new FormData()
                formData.append("file", values[imageName])
                formData.append("description", treatmentNoteResponse.data.id)
                formData.append("patient_id", patient.id)

                try {
                  console.log(`Uploading ${imageName} for patient ${patient.id}...`)
                  const response = await axios.post(`${API}/api/cliniko/patients/${patient.id}/upload-attachment`, formData, {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                  })
                  console.log(`Successfully uploaded ${imageName}`, response.data)
                } catch (error) {
                  console.error(`Failed to upload ${imageName}:`, error)
                  Sentry.captureException(error, {
                    extra: {
                      imageName,
                      patientId: patient.id,
                      treatmentNoteId: treatmentNoteResponse.data.id,
                      formValues: values,
                      formData: formData,
                      component: "OnlineConsultationForm",
                      action: "upload image",
                      error: error,
                      errorMessage: error?.message,
                      errorResponse: JSON.stringify(error?.response),
                    },
                  })
                }
              }
            }

            // api/form-online-consultations
            await axios.post(`${API}/api/form-online-consultations`, {
              ...valuesToSubmit,
              data: {
                ...valuesToSubmit.data,
                hearAboutUs: values.referralSource,
                hearAboutUsDescription: values.referralSourceDescription,
              },
            })

            setSubmitting(false)
            resetForm()
            setFormComplete(true)
            trackConsultationSubmit({ type: "online" })
          } catch (err) {
            console.error("Error during form submission:", err)
            setStatus("Error with submission. Please contact us if the error persists.")
            setSubmitting(false)

            // Capture the error in Sentry
            Sentry.captureException(err, {
              extra: {
                formValues: values,
                component: "OnlineConsultationForm",
                action: "form submission",
                error: err,
                errorMessage: err?.message,
                errorResponse: JSON.stringify(err?.response),
              },
            })
          }
        }}
      >
        <ErrorScroller />
        <Field name="name" label="Name" placeholder="Enter your full name" />
        <Field name="email" label="Email" placeholder="Enter your email" type="email" />
        <PhoneNumberField />
        <DOBField name="dateOfBirth" label="Date of Birth" placeholder="DD/MM/YYYY" />
        <motion.div className="space-y-4" variants={variants}>
          <ImageLabel />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 image-wrappers">
            <ImageField name="imageFront" label="Front" icon={ConsultationFrontIcon} />
            <ImageField name="imageTop" label="Top" icon={ConsultationTopIcon} />
            <ImageField name="imageLeft" label="Left" icon={ConsultationLeftIcon} />
            <ImageField name="imageRight" label="Right" icon={ConsultationRightIcon} />
          </div>
        </motion.div>
        <Field name="message" label="Message" placeholder="Tell us about your expectations or any other useful information" as="textarea" />
        <HowDidYouHearField />
      </Form>
    </motion.div>
  )
}

export default OnlineConsultationForm
